import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  booleanAttribute,
  computed,
  effect,
  input,
  output,
  signal,
  viewChildren,
} from '@angular/core';

/**
 * N-digit OTP input. Each digit lives in its own <input> for auto-focus
 * advancement, backspace-to-prev, and multi-digit paste handling.
 */
@Component({
  selector: 'vh-otp-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="vh-otp" [attr.data-disabled]="disabled()">
      @for (slot of slots(); track $index) {
        <input
          #slotInput
          class="vh-otp__slot"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="1"
          [disabled]="disabled()"
          [value]="digits()[$index] ?? ''"
          (input)="handleInput($index, $event)"
          (keydown)="handleKeydown($index, $event)"
          (paste)="handlePaste($event)"
        />
      }
    </div>
  `,
  styleUrl: './otp-input.component.scss',
})
export class VhOtpInputComponent {
  readonly length = input(6);
  readonly disabled = input(false, { transform: booleanAttribute });

  readonly completed = output<string>();
  readonly valueChange = output<string>();

  private readonly _digits = signal<string[]>([]);
  readonly digits = this._digits.asReadonly();

  readonly slots = computed(() => Array.from({ length: this.length() }));

  private readonly inputs = viewChildren<ElementRef<HTMLInputElement>>('slotInput');

  constructor() {
    // Reset digits if length changes.
    effect(() => {
      const len = this.length();
      this._digits.update((prev) => {
        if (prev.length === len) return prev;
        const next = new Array<string>(len).fill('');
        for (let i = 0; i < Math.min(prev.length, len); i++) next[i] = prev[i];
        return next;
      });
    });
  }

  /** Programmatically clear all digits. Useful after a failed verify. */
  reset(): void {
    this._digits.set(new Array<string>(this.length()).fill(''));
    this.focusIndex(0);
  }

  handleInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const char = target.value.replace(/\D/g, '').slice(-1);
    target.value = char;

    const next = [...this._digits()];
    next[index] = char;
    this._digits.set(next);

    this.emit();

    if (char && index < this.length() - 1) {
      this.focusIndex(index + 1);
    }
  }

  handleKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace') {
      const digits = this._digits();
      if (!digits[index] && index > 0) {
        event.preventDefault();
        const next = [...digits];
        next[index - 1] = '';
        this._digits.set(next);
        this.focusIndex(index - 1);
        this.emit();
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusIndex(index - 1);
    } else if (event.key === 'ArrowRight' && index < this.length() - 1) {
      event.preventDefault();
      this.focusIndex(index + 1);
    }
  }

  handlePaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    const clean = pasted.replace(/\D/g, '').slice(0, this.length());
    if (!clean) return;
    event.preventDefault();

    const next = new Array<string>(this.length()).fill('');
    for (let i = 0; i < clean.length; i++) next[i] = clean[i];
    this._digits.set(next);
    this.emit();

    const focusAt = Math.min(clean.length, this.length() - 1);
    this.focusIndex(focusAt);
  }

  private focusIndex(i: number): void {
    const el = this.inputs()[i]?.nativeElement;
    el?.focus();
    el?.select();
  }

  private emit(): void {
    const value = this._digits().join('');
    this.valueChange.emit(value);
    if (value.length === this.length() && !value.includes('')) {
      this.completed.emit(value);
    }
  }
}
